import React, { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

export default function ManagerChatModal({ open, onClose }) {
  const { profile } = useApp();
  const manager = profile?.manager || { name: "Your Manager", role: "Growth Strategist", photo: "" };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.getMessages(profile.id).then(setMessages).catch(() => setMessages([]));

  useEffect(() => {
    if (open && profile) load();
  }, [open, profile]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  if (!open) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    try {
      const msg = await api.sendMessage(profile.id, "client", text);
      setMessages((m) => [...m, msg]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-lg relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ height: "640px" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-900/40 border border-blue-500/50 overflow-hidden flex items-center justify-center text-blue-300 font-bold">
              {manager.photo ? <img src={manager.photo} alt={manager.name} className="w-full h-full object-cover" /> : manager.name[0]}
            </div>
            <div>
              <h3 className="text-base font-bold text-white">{manager.name}</h3>
              <p className="text-[11px] text-blue-400 flex items-center"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-1.5" /> {manager.role}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 hide-scrollbar">
          {messages.length === 0 && (
            <div className="text-center text-zinc-500 text-sm mt-10">
              <p className="text-white font-bold mb-1">Message {manager.name.split(" ")[0]}</p>
              <p>Send a message and your account manager will reply here.</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.sender === "client" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-sm"}`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-2 bg-black/30">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Message ${manager.name.split(" ")[0]}…`} className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" />
          <button type="submit" disabled={sending || !input.trim()} className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-xl hover:bg-primary-500 transition disabled:opacity-40"><Send size={18} /></button>
        </form>
      </div>
    </div>
  );
}
